import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Button, Accordion, Alert } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import FloatingActionButton from "./FloatingActionButton";
import { useContent } from "../hooks/useContent";
import Footer from "./footer";
import "./Stays.css";
import googleLogo from "../assets/google.png";
import tripadvisorLogo from "../assets/tripadv.png";

// Star Icons
const FullStar = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
      fill="#fbbf24"
    />
  </svg>
);





// Static content moved to module-level constants to avoid allocations on every render
const PROCESS_STEPS = [
  {
    number: 1,
    title: "Choose Your Stay",
    desc: "Browse our unique accommodations and select the perfect one for your nature retreat.",
  },
  {
    number: 2,
    title: "Select Dates",
    desc: "Pick your arrival and departure dates to check availability and pricing.",
  },
  {
    number: 3,
    title: "Connect & Confirm",
    desc: "You’ll be redirected to WhatsApp or Email, where our team will confirm your booking and assist with payment.",
  },
];

const TESTIMONIALS = [
  {
    rating: 5,
    text: "The farm-to-table experience was incredible. Waking up to misty mountains and the sound of birds was magical. We'll definitely be back!",
    author: "Priya & Raj",
    location: "Mumbai, India",
  },
  {
    rating: 5,
    text: "Our kids loved the farm activities! The guided nature walks were educational and fun. The accommodation was cozy with stunning views.",
    author: "Thomas Family",
    location: "London, UK",
  },
  {
    rating: 4.5,
    text: "Perfect getaway for digital detox. No WiFi forced us to connect with nature and each other. The sunset views from our room were breathtaking.",
    author: "Michael & Sarah",
    location: "New York, USA",
  },
];

const FAQ_ITEMS = [
  {
    question: "What time is check-in and check-out?",
    answer:
      "Check-in time is 2:00 PM and check-out is 11:00 AM. Early check-in or late check-out may be available upon request, subject to availability and additional charges.",
  },
  {
    question: "Are pets allowed?",
    answer:
      "We love animals, but to preserve our natural habitat and ensure the comfort of all guests, pets are not allowed in our accommodations. Service animals are welcome with prior notification.",
  },
  {
    question: "Is WiFi available?",
    answer:
      "We intentionally limit WiFi access to encourage our guests to disconnect and immerse themselves in nature. Limited WiFi is available in the main lodge area during specific hours.",
  },
  {
    question: "What is your cancellation policy?",
    answer:
      "Cancellations made 30+ days before arrival will receive a full refund. 15-30 days: 50% refund. Less than 15 days: no refund. We recommend travel insurance for unexpected circumstances.",
  },
];

// Improved RoomCard: Airbnb-like card with location, price, rating and hover elevation
// Helpers moved to module-level to avoid recreating functions per render
const IMG_STYLE = { imageRendering: "auto", transform: "translateZ(0)" };
// no placeholder data-uri: cards will simply not render an image area

const isImageString = (s) => {
  if (typeof s !== "string") return false;
  const t = s.trim();
  if (!t) return false;
  return (
    /^(https?:\/\/|data:|blob:|\/)/i.test(t) ||
    /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i.test(t)
  );
};

const findImageInValue = (val, seen = new WeakSet()) => {
  if (val == null) return null;
  if (typeof val === "string") return isImageString(val) ? val.trim() : null;
  if (Array.isArray(val)) {
    for (const item of val) {
      const res = findImageInValue(item, seen);
      if (res) return res;
    }
    return null;
  }
  if (typeof val === "object") {
    if (seen.has(val)) return null;
    seen.add(val);
    const keys = [
      "url",
      "src",
      "path",
      "downloadURL",
      "image",
      "filename",
      "fullPath",
      "fileUrl",
      "file",
      "storagePath",
    ];
    for (const k of keys) {
      if (val[k]) {
        const res = findImageInValue(val[k], seen);
        if (res) return res;
      }
    }
    for (const k in val) {
      if (!Object.prototype.hasOwnProperty.call(val, k)) continue;
      const res = findImageInValue(val[k], seen);
      if (res) return res;
    }
  }
  return null;
};

// collect all image-like strings from a value (array/object/string)
const collectImages = (val, seen = new WeakSet(), out = []) => {
  if (val == null) return out;
  if (typeof val === "string") {
    const s = val.trim();
    if (isImageString(s)) out.push(s);
    return out;
  }
  if (Array.isArray(val)) {
    for (const item of val) collectImages(item, seen, out);
    return out;
  }
  if (typeof val === "object") {
    if (seen.has(val)) return out;
    seen.add(val);
    const keys = [
      "url",
      "src",
      "path",
      "downloadURL",
      "image",
      "filename",
      "fullPath",
      "fileUrl",
      "file",
      "storagePath",
    ];
    for (const k of keys) {
      if (val[k]) collectImages(val[k], seen, out);
    }
    for (const k in val) {
      if (!Object.prototype.hasOwnProperty.call(val, k)) continue;
      collectImages(val[k], seen, out);
    }
  }
  return out;
};

const formatNumber = (n) => {
  try {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } catch {
    return n;
  }
};

const RoomCard = React.memo(({ room }) => {
  const navigate = useNavigate();

  // gather candidate images (in order found) from room images/photos/object
  const images = useMemo(() => {
    const imgList = [];
    collectImages(room.images, new WeakSet(), imgList);
    if (imgList.length === 0)
      collectImages(room.photos, new WeakSet(), imgList);
    if (imgList.length === 0) collectImages(room, new WeakSet(), imgList);
    // deduplicate while preserving order
    const seen = new Set();
    const result = imgList.filter((s) => {
      if (!s) return false;
      if (seen.has(s)) return false;
      seen.add(s);
      return true;
    });
    // Room images processed
    return result;
  }, [room]);

  const [imgIndex, setImgIndex] = React.useState(0);
  const [showImage, setShowImage] = React.useState(true);

  // reset index/show when room changes
  React.useEffect(() => {
    setImgIndex(0);
    setShowImage(true);
  }, [room && room.id]);

  const onCardClick = useCallback(
    () => navigate(`/stays/${room.id}`),
    [navigate, room.id]
  );

  // Prefer a valid discounted offer if present; fall back to actual price.
  // This makes offer price show even if the flag wasn't toggled, without affecting other pages.
  const actual = Number(room.actualPrice) || 0;
  const offer = Number(room.offerPrice);
  const hasOffer = Number.isFinite(offer) && offer > 0 && offer < actual;
  const price = hasOffer ? offer : actual;

  return (
    <article
      className="stays-card compact-card"
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${room.name}`}
    >
      {showImage && images && images.length > 0 ? (
        <div className="stays-img-wrap compact-img">
          <img
            src={images[imgIndex]}
            alt={room.name}
            className="stays-img compact-img"
            loading="lazy"
            decoding="async"
            width={420}
            height={300}
            sizes="(max-width:480px) 90vw, (max-width:1100px) 45vw, 20vw"
            srcSet={
              typeof images[imgIndex] === "string" &&
              !images[imgIndex].startsWith("data:")
                ? `${images[imgIndex]} 1x`
                : undefined
            }
            importance="low"
            onError={() => {
              // try next image in the list; if none left, hide the image area
              setImgIndex((i) => {
                const next = i + 1;
                if (next < images.length) return next;
                setShowImage(false);
                return i;
              });
            }}
            style={IMG_STYLE}
          />
          {room.isFavourite && (
            <div className="badge-pill">Guest favourite</div>
          )}
        </div>
      ) : null}

      <div className="stays-meta compact-meta">
        <div className="stays-title">{room.name}</div>
        <div className="stays-sub">
          ₹{formatNumber(price)} for 1 nights &nbsp;·&nbsp;
        </div>
      </div>
    </article>
  );
});

RoomCard.displayName = "RoomCard";

const Stays = () => {
  const navigate = useNavigate();
  const {
    content,
    loading: contentLoading,
    error: contentError,
  } = useContent("stays");
  const [heroImage, setHeroImage] = useState("");
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [useStaticHero, setUseStaticHero] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  // Extract content with fallbacks
  const heroContent = content?.hero || {
    title: "Experience Nature's Luxury",
    subtitle: "Find Your Perfect Stay at Ampthill Resort",
    backgroundImage: "",
  };
  const bookingProcessContent = content?.bookingProcess || {
    title: "Simple Booking Process",
    steps: PROCESS_STEPS,
  };
  const testimonialsTitle = content?.testimonialsTitle || "Guest Experiences";
  const testimonialsContent = content?.testimonials || [];
  const faqTitle = content?.faqTitle || "Frequently Asked Questions";
  const faqContent = content?.faq || [];
  const ctaContent = content?.cta || {
    title: "Ready for Your Nature Retreat?",
    description: "",
    primaryButtonText: "Book Your Stay",
    secondaryButtonText: "Contact Us",
  };

  // Firebase-first hero image: preload Firestore URL, fallback to static CSS background
  useEffect(() => {
    let cancelled = false
    setUseStaticHero(false)
    if (contentLoading) return () => { cancelled = true }

    const url = (content?.hero?.backgroundImage || "").trim();
    if (!url) {
      // No Firebase URL; fall back to CSS static
      setHeroImage("");
      setUseStaticHero(true);
      setHeroLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setHeroImage(url);
      setHeroLoaded(true);
    };
    img.onerror = () => {
      if (cancelled) return;
      // leave empty to allow CSS static background as fallback
      setHeroImage("");
      setUseStaticHero(true);
      setHeroLoaded(true);
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [contentLoading, content?.hero?.backgroundImage, content?.hero]);

  

  // booking modal moved to the detail page; not needed here

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const roomsRef = collection(db, 'rooms')
        // Fetch rooms marked as available or under maintenance (case tolerant)
        const q = query(
          roomsRef,
          where('status', 'in', [
            'AVAILABLE',
            'available',
            'MAINTENANCE',
            'maintenance',
          ])
        )
        const snapshot = await getDocs(q)
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        // Loaded rooms from Firestore
        setRooms(data);
      } catch (err) {
        // Firebase error occurred
        setRoomsError(`Failed to load rooms: ${err.message}`);
      } finally {
        setRoomsLoading(false);
      }
    };
    fetchRooms();
  }, []);

  const filteredRooms = useMemo(
    () =>
      rooms.filter((room) => {
        if (activeFilter === "all") return true;
        if (activeFilter === "meals" && room.mealsIncluded) return true;
        if (activeFilter === "farm" && room.farmAccess) return true;
        if (activeFilter === "activities" && room.activitiesIncluded)
          return true;
        return false;
      }),
    [rooms, activeFilter]
  );

  // Show compact cards; booking handled on detail page

  // Removed content spinner: render page immediately with fallback content.

  // Show content error state
  if (contentError) {
    return (
      <div className="stays-page">
        <Alert variant="danger" className="text-center mb-0">
          <strong>Content Loading Error:</strong> {contentError}
          <p className="mt-2">Please try refreshing the page.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="stays-page">
      {roomsError && (
        <div className="alert alert-danger text-center mb-0" role="alert">
          <strong>Error:</strong> {roomsError}
          <p className="mt-2">
            Please try refreshing the page or contact support if the problem
            persists.
          </p>
        </div>
      )}

      {/* Hero Section */}
      <section className='stays-hero-section'>
        {!contentLoading && !useStaticHero && heroImage && (
          <img 
            src={heroImage} 
            alt="Hero" 
            className='hero-image-tag'
            loading='eager'
            fetchpriority='high'
            decoding='sync'
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
          />
        )}
        <div className={`hero-overlay ${heroLoaded ? 'is-loaded' : ''}`}>
          <div className='hero-content container text-center'>
            <h1 className='hero-title'>{heroContent.title}</h1>
            <p className='hero-subtitle'>{heroContent.subtitle}</p>
          </div>
        </div>
      </section>
      {/* Room Cards */}
      <section className="rooms-section py-5">
        <Container>
          {roomsLoading ? (
            <div className="text-center py-5">
              <p className="mt-3">Loading rooms...</p>
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center py-5">
              <h3>No rooms match your filter criteria</h3>
            </div>
          ) : (
            <div className="stays-list-row">
              {filteredRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Booking Process */}
      <section className="booking-process py-5 bg-light">
        <Container>
          <h2 className="section-head">{bookingProcessContent.title}</h2>
          <Row className="g-4 justify-content-center text-center">
            {bookingProcessContent.steps.map((step, index) => (
              <Col md={4} key={step.number || index}>
                <div className="process-step p-3 h-100 d-flex flex-column align-items-center justify-content-center shadow-sm rounded bg-white">
                  <div className="step-number mb-3">{step.number}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="testimonials py-5">
        <Container>
          <h2 className="section-head">{testimonialsTitle}</h2>
          <Row className="g-4 justify-content-center">
            {testimonialsContent.map((t, i) => {
              const name = t.author || "Guest";
              const initials =
                name
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((s) => s[0]?.toUpperCase())
                  .join("") || "G";
              const rating = Math.max(0, Math.min(5, Number(t.rating ?? 5)));
              const source = String(t.source || "").toLowerCase();
              const sourceLogo = source.includes("google")
                ? googleLogo
                : source.includes("trip")
                ? tripadvisorLogo
                : null;

              return (
                <Col md={6} lg={4} key={i}>
                  <div className="stays-testimonial-card p-4 h-100 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center">
                        <div className="stays-avatar-circle me-3">
                          <span>{initials}</span>
                        </div>
                        <div>
                          <h5 className="stays-author-name mb-0">{name}</h5>
                          <small className="stays-author-loc text-muted">
                            {t.location || ""}
                          </small>
                        </div>
                      </div>
                      {sourceLogo && (
                        <div className="stays-review-source-logo">
                          <img src={sourceLogo} alt="Source" />
                        </div>
                      )}
                    </div>

                    <div className="stays-star-rating mb-3">
                      {Array.from({ length: rating }).map((_, idx) => (
                        <span key={`full-${idx}`} className="stays-star">
                          ★
                        </span>
                      ))}
                      {Array.from({ length: 5 - rating }).map((_, idx) => (
                        <span key={`empty-${idx}`} className="stays-star empty">
                          ★
                        </span>
                      ))}
                    </div>

                    <p className="stays-testimonial-quote flex-grow-1">
                      “{t.text}”
                    </p>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section className="faq-section py-5 bg-light" id="frequently-asked">
        <Container>
          <h2 className="section-head">{faqTitle}</h2>
          <Accordion defaultActiveKey="0" className="faq-accordion">
            {faqContent.map((faq, i) => (
              <Accordion.Item eventKey={i.toString()} key={i}>
                <Accordion.Header>
                  <p className="faq-ques">{faq.question}</p>
                </Accordion.Header>
                <Accordion.Body>
                  <p>{faq.answer}</p>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="sectionCta py-5 text-center">
        <Container>
          <h2 className="section-head">{ctaContent.title}</h2>
          <p className="cta-text mb-4">{ctaContent.description}</p>
          <div className="stays-book-btns">
            <button
              variant="primary"
              size="lg"
              className="stays-book-btn"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              {ctaContent.primaryButtonText}
            </button>
            <button
              variant="outline-primary"
              size="lg"
              className="stays-book-btn"
              onClick={() => navigate("/contact")}
            >
              {ctaContent.secondaryButtonText}
            </button>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <Footer />

      <FloatingActionButton />
    </div>
  );
};

export default Stays;
